import type { ReactNode } from "react";
import { Icon } from "./ui";

/** `waiting`: a count of things waiting for the person, shown only above 0. */
/** `solution`: a page another solution adds to this section (shown only where that one is switched on too). */
export type NavSub = { to: string; label: string; also?: readonly string[]; waiting?: { count: string; title: string }; solution?: string };
export type NavItem = {
  to: string;
  label: string;
  icon: () => ReactNode;
  also?: readonly string[];
  except?: readonly string[];
  subs?: readonly NavSub[];
  /** The permission that opens it (any of them). */
  need?: string | readonly string[];
  /** The solution that brings it (shown only where it's switched on). */
  solution?: string;
  /** A count of things waiting for the person, shown beside it above 0 (the shell supplies the counts). */
  waiting?: { count: string; title: string };
};

/**
 * Every area of the admin, in sidebar order. A solution's area shows only where the business has it
 * switched on; a section shows its pages only while you are in it.
 */
export const NAV: readonly NavItem[] = [
  { to: "/", label: "Home", icon: Icon.home },
  // Mivio's own: everything waiting for a yes, for everyone signed in (each sees what their role can decide).
  { to: "/agents/approvals", label: "Approvals", icon: Icon.check, waiting: { count: "approvals", title: "Waiting for you" } },
  { to: "/events", label: "Events", icon: Icon.run, solution: "events", need: "events.view", also: ["/check-in", "/giveaways"], subs: [{ to: "/check-in", label: "Check-in" }, { to: "/giveaways", label: "Giveaways", solution: "giveaways" }] },
  { to: "/people", label: "People", icon: Icon.people, solution: "people", need: "people.view" },
  {
    to: "/sales",
    label: "Sales",
    icon: Icon.briefcase,
    solution: "sales",
    need: "sales.view",
    subs: [
      { to: "/sales/inbox", label: "Inbox" },
      { to: "/sales/companies", label: "Companies" },
      { to: "/sales/people", label: "Contacts" },
      { to: "/sales/agreements", label: "Agreements" },
    ],
  },
  { to: "/collaborations", label: "Collaborations", icon: Icon.share, solution: "collaborations", need: "collaborations.view" },
  { to: "/partners", label: "Partners", icon: Icon.flag, solution: "partners", need: "partners.view" },
  {
    to: "/meetings",
    label: "Meetings",
    icon: Icon.meeting,
    solution: "meetings",
    need: "meetings.view",
    subs: [
      { to: "/meetings/todos", label: "To-dos" },
      { to: "/meetings/voices", label: "Who's who" },
    ],
  },
  {
    to: "/marketing",
    label: "Marketing",
    icon: Icon.mail,
    solution: "marketing",
    need: "marketing.view",
    subs: [
      { to: "/marketing/campaigns", label: "Campaigns" },
      { to: "/marketing/automations", label: "Automations" },
      { to: "/marketing/subscribers", label: "Subscribers" },
      { to: "/marketing/emails", label: "Email log" },
    ],
  },
  {
    to: "/social",
    label: "Social",
    icon: Icon.megaphone,
    solution: "social",
    need: "social.view",
    subs: [
      { to: "/social/planner", label: "Planner" },
      { to: "/social/published", label: "Published" },
      { to: "/social/comments", label: "Comments" },
      { to: "/social/messages", label: "Messages" },
      { to: "/social/insights", label: "Insights" },
      { to: "/social/channels", label: "Channels" },
      { to: "/social/links", label: "Profile links" },
      { to: "/social/winners", label: "Message winners" },
    ],
  },
  {
    to: "/newsroom",
    label: "Newsroom",
    icon: Icon.doc,
    solution: "newsroom",
    need: "newsroom.view",
    subs: [
      { to: "/newsroom/plan", label: "Content plan" },
      { to: "/newsroom/drafted", label: "Drafted" },
      { to: "/newsroom/keywords", label: "Keywords" },
    ],
  },
  { to: "/media", label: "Media", icon: Icon.image, solution: "media", need: "media.view", also: ["/gallery"] },
  {
    to: "/orders",
    label: "Shop",
    icon: Icon.tag,
    solution: "shop",
    need: "shop.view",
    also: ["/products", "/collections", "/inventory", "/customers", "/discounts", "/abandoned-carts", "/reviews"],
    subs: [
      { to: "/orders", label: "Orders" },
      { to: "/abandoned-carts", label: "Abandoned carts" },
      { to: "/products", label: "Products" },
      { to: "/collections", label: "Collections" },
      { to: "/inventory", label: "Inventory" },
      { to: "/customers", label: "Customers" },
      { to: "/reviews", label: "Reviews" },
      { to: "/discounts", label: "Discounts" },
    ],
  },
  {
    to: "/finance",
    label: "Finance",
    icon: Icon.bank,
    solution: "finance",
    need: "finance.view",
    subs: [
      { to: "/finance/invoices", label: "Invoices" },
      { to: "/finance/bills", label: "Bills" },
      { to: "/finance/bank", label: "Bank accounts" },
      { to: "/finance/profit-and-loss", label: "Profit and loss" },
      { to: "/finance/spend", label: "Spend" },
    ],
  },
  {
    to: "/analytics",
    label: "Analytics",
    icon: Icon.chart,
    solution: "analytics",
    need: "analytics.view",
    subs: [
      { to: "/analytics/traffic", label: "Traffic" },
      { to: "/analytics/shop", label: "Shop" },
      { to: "/analytics/live", label: "Live View" },
    ],
  },
  // Mivio's own: the agents, what they did and their guardrails (owners and administrators).
  {
    to: "/agents",
    label: "Agents",
    icon: Icon.sparkle,
    need: "business.agents",
    except: ["/agents/approvals"],
    subs: [
      { to: "/agents", label: "Staff" },
      { to: "/agents/history", label: "History" },
      { to: "/agents/settings", label: "Guardrails" },
    ],
  },
  {
    to: "/loops",
    label: "Loops",
    icon: Icon.refresh,
    solution: "loops",
    need: "loops.view",
    subs: [
      { to: "/loops/members", label: "Members" },
      { to: "/loops/leaderboard", label: "Leaderboard" },
      { to: "/loops/inventory", label: "Inventory" },
      { to: "/loops/agent", label: "Agent" },
      { to: "/loops/designs", label: "Designs", waiting: { count: "loops.designs", title: "Card designs waiting for a look" } },
      { to: "/loops/races", label: "Races" },
      { to: "/loops/running", label: "Running" },
      { to: "/loops/shop", label: "Shop and pick-ups", also: ["/loops/pickup", "/loops/host", "/loops/tap"] },
      { to: "/loops/rules", label: "Rules" },
    ],
  },
  { to: "/recaps", label: "Recap cards", icon: Icon.image, solution: "recaps", need: "recaps.view" },
  {
    to: "/online-store",
    label: "Online Store",
    icon: Icon.store,
    solution: "website",
    need: "website.view",
    also: ["/pages", "/templates"],
    subs: [
      { to: "/pages", label: "Pages" },
      { to: "/online-store/menus", label: "Menus" },
      { to: "/templates", label: "Page layouts" },
      { to: "/online-store/preferences", label: "Preferences" },
    ],
  },
];

export function navFor(solutionsOn: readonly string[]): NavItem[] {
  return NAV.filter((n) => !n.solution || solutionsOn.includes(n.solution))
    .map((n) => (n.subs?.some((x) => x.solution) ? { ...n, subs: n.subs.filter((x) => !x.solution || solutionsOn.includes(x.solution)) } : n));
}
